export const CHANGE_RATE_RMB_TO_USD = 0.14;

// Funciones de utilidad para conversión de monedas
export const RMB_TO_USD = (rmb: number): number => rmb * CHANGE_RATE_RMB_TO_USD;
export const USD_TO_RMB = (usd: number): number => usd / CHANGE_RATE_RMB_TO_USD;

export const MARKETS = ['all', 'GZ', 'SH', 'JX'];
export const MARKET_LABELS: { [key: string]: string } = {
  all: 'Todos',
  GZ: 'Guangzhou',
  SH: 'Shanghai',
  JX: 'Jiaxing',
};

export const VIEW_MODES = {
  all: 'Todos',
  ship: 'Por Naves',
  week: 'Por Semana Venta',
};

export const ALLOWED_VARIETIES = [
  'santina',
  'rainier',
  'royal dawn',
  'lapins',
  'regina',
  'bing',
  'kordia',
  'sweetheart',
  'skeena',
];

import { TableDefinition } from 'common/src/utils/types';

export const CHERRY_WEEKS = {
  weeks: [
    42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
  ],
  defaultWeek: 50,
};

export const CHINESE_NEW_YEARS = {
  season2021: new Date('2021-02-12'),
  season2022: new Date('2022-02-01'),
  season2023: new Date('2023-01-22'),
  season2024: new Date('2024-02-10'),
  season2025: new Date('2025-01-29'),
  season2026: new Date('2026-02-17'),
  season2027: new Date('2027-02-06'),
  season2028: new Date('2028-01-26'),
  season2029: new Date('2029-02-13'),
  season2030: new Date('2030-02-03'),
};

export const SPECIES_ID_TO_FRUTIC_SPECIES = {
  2: 1, // CEREZA
  3: 10, //NECTARINE
  4: 2, // SUGAR PLUMS
  6: 2, // PLUMS
  8: 3, // KIWIS
  9: 28, // GRAPE
  10: 4, // APPLES
};
// FRUTIC SPECIES
// 1	🍒 Cerezas
// 2	🟣 Ciruelas
// 3	🥝 Kiwis
// 4	🍎 Manzanas
// 5	🍐 Peras

export const SEASONS = {
  season2019_2020: {
    name: '2019-2020',
    start: new Date('2019-07-01'),
    end: new Date('2020-06-30'),
  },
  season2020_2021: {
    name: '2020-2021',
    start: new Date('2020-07-01'),
    end: new Date('2021-06-30'),
  },
  season2021_2022: {
    name: '2021-2022',
    start: new Date('2021-07-01'),
    end: new Date('2022-06-30'),
  },
  season2022_2023: {
    name: '2022-2023',
    start: new Date('2022-07-01'),
    end: new Date('2023-06-30'),
  },
  season2023_2024: {
    name: '2023-2024',
    start: new Date('2023-07-01'),
    end: new Date('2024-06-30'),
  },
  season2024_2025: {
    name: '2024-2025',
    start: new Date('2024-07-01'),
    end: new Date('2025-06-30'),
  },
  season2025_2026: {
    name: '2025-2026',
    start: new Date('2025-07-01'),
    end: new Date('2026-06-30'),
  },
  season2026_2027: {
    name: '2026-2027',
    start: new Date('2026-07-01'),
    end: new Date('2027-06-30'),
  },
} as const;

/**
 * Temporada vigente para la app: la que se preselecciona y contra la que se
 * compara para avisar "estás en otra temporada".
 *
 * A propósito NO es "la última de SEASONS": la temporada nueva se agrega a la
 * lista apenas empieza el calendario (1 de julio) para que quede disponible en
 * los selectores, pero recién pasa a ser la vigente cuando tiene datos
 * cargados. Cuando eso ocurra, se cambia esta línea y listo.
 */
export const CURRENT_SEASON = '2026-2027';

/**
 * Origen preseleccionado en los filtros de mercado.
 *
 * El dataset mezcla fruta de varios hemisferios: entre las semanas 24 y 31 no
 * hay ninguna fila chilena y sí de USA/Washington/Canadá, cotizada al doble.
 * Mostrar todo junto deforma la curva, así que se parte filtrado por Chile y
 * quien quiera comparar orígenes lo cambia a mano.
 */
export const DEFAULT_ORIGIN = 'CHILE';

export const QUARTILE_COLORS = {
  '0': 'bg-default-500',
  '1': 'bg-red-500', // Q1 - Worst performing (red)
  '2': 'bg-yellow-500', // Q2 - Below average (yellow)
  '3': 'bg-green-500', // Q3 - Above average (blue)
  '4': 'bg-blue-500', // Q4 - Best performing (green)
} as const;

export const VALID_FORMATS = [
  'CAJA',
  '2IN1',
  '3IN1',
  '4IN1',
  '5IN1',
  '6IN1',
  '7IN1',
  '8IN1',
  '9IN1',
  '10IN1',
  '11IN1',
  '12IN1',
];

export const TABLES_CONFIG: Record<string, TableDefinition> = {
  settlements: {
    name: 'Liquidaciones',
    columns: [
      { name: 'Recibidor', key: 'receiver', type: 'string' },
      { name: 'Región', key: 'region', type: 'string' },
      { name: 'Temporada', key: 'season', type: 'string' },
    ],
    hasExporter: true,
  },
  settlements_details: {
    name: 'Detalles de Liquidaciones',
    columns: [
      { name: 'Formato', key: 'format', type: 'string' },
      { name: 'Etiqueta', key: 'label', type: 'string' },
      { name: 'Variedad', key: 'variety', type: 'string' },
      { name: 'Calibre', key: 'caliber', type: 'string' },
      { name: 'Color', key: 'color', type: 'string' },
      { name: 'Pallet', key: 'pallet', type: 'string' },
      { name: 'Moneda', key: 'local_currency', type: 'string' },
      { name: 'Incoterm', key: 'incoterm', type: 'string' },
      { name: 'Fecha de Venta', key: 'date_sold', type: 'date' },
    ],
    hasExporter: true,
  },
  ships: {
    name: 'Naves',
    columns: [
      { name: 'Nombre', key: 'name', type: 'string' },
      { name: 'Compañía', key: 'company', type: 'string' },
      { name: 'Tipo', key: 'type', type: 'string' },
    ],
  },
  market_data: {
    name: 'Datos de Mercado',
    columns: [
      { name: 'Fecha', key: 'date', type: 'date' },
      { name: 'Reporte', key: 'report', type: 'string' },
      { name: 'Temporada', key: 'season', type: 'string' },
      { name: 'Semana', key: 'week', type: 'string' },
      { name: 'Región', key: 'region', type: 'string' },
      { name: 'Item', key: 'item', type: 'string' },
      { name: 'Transporte', key: 'transport', type: 'string' },
      { name: 'Origen', key: 'origin', type: 'string' },
      { name: 'Marca', key: 'brand', type: 'string' },
      { name: 'Variedad', key: 'variety', type: 'string' },
      { name: 'Volumen', key: 'volume', type: 'number' },
      { name: 'Volumen Vendido', key: 'sold_volume', type: 'number' },
      { name: 'Empaque', key: 'package', type: 'string' },
      { name: 'Clase', key: 'class', type: 'string' },
      { name: 'Vendedor', key: 'seller', type: 'string' },
      { name: 'Dueño', key: 'owner', type: 'string' },
      { name: 'Calidad', key: 'quality', type: 'string' },
      { name: 'Nuevo', key: 'new', type: 'string' },
      { name: 'Precio', key: 'price', type: 'number' },
      { name: 'Tamaño', key: 'size', type: 'string' },
      { name: 'Color', key: 'color', type: 'string' },
      { name: 'Kg', key: 'kg', type: 'number' },
      { name: 'Peso (kg)', key: 'weight_kg', type: 'string' },
      { name: 'Precio por kg', key: 'price_kg', type: 'number' },
      { name: 'Número de Productor', key: 'grower_no', type: 'string' },
    ],
  },
};
